/**
 * CreateNewController - Unified controller for all Create New modals
 * 
 * Renders the appropriate modal based on activeModalKey from context.
 * Passes modalContext to modals that support prefill (e.g., CreateOrderModal).
 * 
 * HARDENING (P1.1):
 * - Uses normalized context shape from CreateNewContext
 * - accountId is source of truth for API submission
 * - _display fields are for UI only
 */
import React from 'react';
import { useCreateNew, MODAL_KEYS } from './CreateNewContext';

// Import all create modals
import CreateOrderModal from './CreateOrderModal';
import CreateCustomerModal from './CreateCustomerModal';
import CreateContactModal from './CreateContactModal';
import CreateVendorModal from './CreateVendorModal';
import CreateProductModal from './CreateProductModal';
import CreatePurchaseOrderModal from './CreatePurchaseOrderModal';
import CreateBillModal from './CreateBillModal';
import CreatePaymentModal from './CreatePaymentModal';

const CreateNewController = ({ 
  onOrderSuccess,
  onCustomerSuccess,
  onContactSuccess,
  onVendorSuccess,
  onProductSuccess,
  onPOSuccess,
  onBillSuccess,
  onPaymentSuccess,
  accounts = [], // Pass accounts list for contact modal
}) => {
  const { activeModalKey, modalContext, closeCreateModal } = useCreateNew();

  /**
   * Build prefill customer object from normalized modalContext
   * Uses accountId as source of truth, _display for UI
   */
  const getOrderPrefillCustomer = () => {
    if (!modalContext) return null;
    
    // If prefillFromOrderIds is set, don't provide prefillCustomer
    // (customer will be fetched from orders)
    if (modalContext.prefillFromOrderIds && modalContext.prefillFromOrderIds.length > 0) {
      return null;
    }
    
    const { accountId, _display } = modalContext;
    if (!accountId) return null;
    
    return {
      // Source of truth - this ID is used for API submission
      id: accountId,
      // Display fields - for UI rendering only
      name: _display?.customerName || '',
      email: _display?.email || '',
      phone: _display?.phone || '',
    };
  };

  /**
   * Get prefillFromOrderIds from context (for reorder/combine feature)
   */
  const getOrderPrefillFromOrderIds = () => {
    if (!modalContext) return null;
    return modalContext.prefillFromOrderIds || null;
  };

  return (
    <>
      {/* Create Order Modal */}
      <CreateOrderModal
        open={activeModalKey === MODAL_KEYS.ORDER}
        onOpenChange={(open) => !open && closeCreateModal()}
        prefillCustomer={getOrderPrefillCustomer()}
        prefillFromOrderIds={getOrderPrefillFromOrderIds()}
        onSuccess={(orderId) => {
          closeCreateModal();
          onOrderSuccess?.(orderId);
        }}
      />

      {/* Create Customer Modal */}
      <CreateCustomerModal
        open={activeModalKey === MODAL_KEYS.CUSTOMER}
        onOpenChange={(open) => !open && closeCreateModal()}
        onSuccess={(customerId) => {
          closeCreateModal();
          onCustomerSuccess?.(customerId);
        }}
      />

      {/* Create Contact Modal */}
      <CreateContactModal
        isOpen={activeModalKey === MODAL_KEYS.CONTACT}
        onClose={closeCreateModal}
        onSuccess={(contact) => {
          closeCreateModal();
          onContactSuccess?.(contact);
        }}
        prefilledAccountId={modalContext?.accountId}
        accounts={accounts}
      />

      {/* Create Vendor Modal */}
      <CreateVendorModal
        open={activeModalKey === MODAL_KEYS.VENDOR}
        onOpenChange={(open) => !open && closeCreateModal()}
        onSuccess={(vendorId) => {
          closeCreateModal();
          onVendorSuccess?.(vendorId);
        }}
      />

      {/* Create Product Modal */}
      <CreateProductModal
        open={activeModalKey === MODAL_KEYS.PRODUCT}
        onOpenChange={(open) => !open && closeCreateModal()}
        onSuccess={(productId) => {
          closeCreateModal();
          onProductSuccess?.(productId);
        }}
      />

      {/* Create Purchase Order Modal */}
      <CreatePurchaseOrderModal
        open={activeModalKey === MODAL_KEYS.PURCHASE_ORDER}
        onOpenChange={(open) => !open && closeCreateModal()}
        prefillVendor={modalContext?.vendorId ? {
          id: modalContext.vendorId,
          name: modalContext.vendorName || '',
        } : null}
        onSuccess={(poId) => {
          closeCreateModal();
          onPOSuccess?.(poId);
        }}
      />

      {/* Create Bill Modal */}
      <CreateBillModal
        open={activeModalKey === MODAL_KEYS.BILL}
        onOpenChange={(open) => !open && closeCreateModal()}
        initialVendorId={modalContext?.vendorId}
        initialVendorName={modalContext?.vendorName}
        initialPoId={modalContext?.poId}
        initialPoNumber={modalContext?.poNumber}
        onSuccess={(billId) => {
          closeCreateModal();
          onBillSuccess?.(billId);
        }}
      />

      {/* Create Payment Modal */}
      <CreatePaymentModal
        open={activeModalKey === MODAL_KEYS.PAYMENT}
        onOpenChange={(open) => !open && closeCreateModal()}
        prefillVendor={modalContext?.vendorId ? {
          id: modalContext.vendorId,
          name: modalContext.vendorName || '',
        } : null}
        prefillBill={modalContext?.billId ? {
          id: modalContext.billId,
          bill_number: modalContext.billNumber,
          amount_cents: modalContext.billAmountCents,
          balance_cents: modalContext.billBalanceCents,
        } : null}
        onSuccess={(paymentId) => {
          closeCreateModal();
          onPaymentSuccess?.(paymentId);
        }}
      />
    </>
  );
};

export default CreateNewController;
